import { Outlet, Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import FomoFiLogo from "@/components/brand/FomoFiLogo";

const HERO_BLUE = "#2196F3";

/** Decorative speckles for the auth background, in the SVG's own 0-100 user
 *  space. A module constant: it is fixed art, so it neither changes nor is
 *  rebuilt on every render, and the index each circle is keyed by is the
 *  position it draws at. */
const AUTH_PATTERN_DOTS: [number, number, number][] = [
  [8, 12, 1],
  [14, 18, 1.2],
  [22, 10, 0.9],
  [18, 28, 1.1],
  [10, 35, 1],
  [24, 40, 0.9],
  [85, 15, 1.2],
  [90, 25, 1],
  [78, 35, 1.1],
  [88, 45, 0.9],
  [82, 55, 1],
  [92, 65, 1.2],
  [75, 70, 0.9],
  [86, 80, 1.1],
  [80, 90, 1],
  [12, 55, 1],
  [18, 70, 0.9],
  [8, 82, 1.1],
  [22, 92, 1],
  [50, 20, 1],
  [60, 60, 1.1],
  [45, 80, 0.9],
  [55, 45, 1],
  [35, 25, 1.1],
  [30, 75, 0.9],
];

function AuthPattern() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
        <defs>
          <radialGradient id="auth-glow-1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D9E7F5" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#F6F9FC" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="auth-glow-2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#DCE3F4" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#F0F3FA" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="auth-wave" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#BFD2E8" stopOpacity="0" />
            <stop offset="50%" stopColor="#A9C2DE" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#BFD2E8" stopOpacity="0" />
          </linearGradient>
        </defs>
        <ellipse cx="20" cy="30" rx="35" ry="28" fill="url(#auth-glow-1)" />
        <ellipse cx="80" cy="75" rx="30" ry="25" fill="url(#auth-glow-2)" />
        <ellipse cx="65" cy="20" rx="22" ry="18" fill="url(#auth-glow-2)" opacity="0.6" />
        <path
          d="M 0 40 Q 25 30, 50 45 T 100 50"
          stroke="url(#auth-wave)"
          strokeWidth="0.5"
          fill="none"
          opacity="0.6"
        />
        <path
          d="M 0 65 Q 30 55, 55 72 T 100 78"
          stroke="url(#auth-wave)"
          strokeWidth="0.5"
          fill="none"
          opacity="0.5"
        />
        {AUTH_PATTERN_DOTS.map(([cx, cy, r], i) => (
          <circle key={i} cx={cx} cy={cy} r={r * 0.18} fill="#D7E1EC" opacity={0.6} />
        ))}
      </svg>
    </div>
  );
}

function BackToHome({ className = "" }: { className?: string }) {
  return (
    <Link
      to="/"
      className={`inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 transition hover:text-neutral-900 dark:text-muted-foreground dark:hover:text-foreground ${className}`}
    >
      <ArrowLeft className="h-4 w-4" />
      Back to home
    </Link>
  );
}

function LeftPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-[#F7FAFD] p-10 lg:flex lg:w-[45%] xl:w-1/2 dark:bg-card">
      <AuthPattern />

      {/* Top row: logo + back */}
      <div className="relative z-10 flex items-center justify-between">
        <Link to="/" aria-label="Fomo-Fi home">
          <FomoFiLogo size="md" />
        </Link>
        <BackToHome />
      </div>

      {/* Middle: phone imagery */}
      <div className="relative z-10 flex flex-1 items-center justify-center py-10">
        <img
          src="/screenshots/phone-hero.png"
          alt=""
          className="max-h-[560px] w-auto drop-shadow-2xl"
          aria-hidden
        />
      </div>

      {/* Bottom tagline */}
      <div className="relative z-10 max-w-md">
        <h2
          className="font-extrabold tracking-tight text-neutral-900 dark:text-foreground"
          style={{
            fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
          }}
        >
          Know what&rsquo;s <span style={{ color: HERO_BLUE }}>safe to spend</span>
          <br />
          before payday hits.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-muted-foreground">
          Forecasts up to 12 months &middot; Paycheck remainder &middot; Debt payoff
        </p>
      </div>
    </div>
  );
}

export default function AuthLayout() {
  // AN-26 (Anthony): sign-up states the agreement once, in its checkbox, so this
  // footer line (the same agreement again) is left off there. Log in keeps it: it's
  // that page's only statement of the terms.
  const { pathname } = useLocation();
  const isSignup = pathname.startsWith("/auth/signup");
  // CALL-06 (phone-capture run, 2026-10-28): sign-up is one phone screen per step, but at
  // 360 (780 tall) it measured 1.07/1.08 and at 375 1.03/1.04. `data-signup` lets the
  // header, band and card trim their padding below 390 wide on sign-up only
  // (a group-data variant on each); log in and the rest, 390, tablets and desktop
  // are unchanged. A viewport query, so 200% text doesn't trigger it.
  return (
    <div
      data-signup={isSignup || undefined}
      className="group flex min-h-screen bg-white dark:bg-background"
    >
      <LeftPanel />

      {/* Right panel: form. Report 161 group 4 (2026-10-27): at 200% text the panel could
          not shrink below its widest word or button (a flex item's min-width is its
          content), so login, sign-up and forgot/reset ran 58-180px past a 360-390 phone,
          taking the card and its "Privacy Policy" footer link with them. `min-w-0` lets it
          take the screen's width; the bar below wraps its logo under "Back to home" when
          both can't share a line, and the card steps its padding down under 16rem (only
          ever at large text: a 360 phone gives the card 20rem at default). */}
      <div className="relative flex min-w-0 flex-1 flex-col">
        {/* Mobile-only top bar: back link + logo */}
        <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-4 group-data-[signup]:max-[24.375rem]:py-2 lg:hidden">
          <BackToHome />
          <Link to="/" aria-label="Fomo-Fi home">
            <FomoFiLogo size="md" />
          </Link>
        </header>

        {/* CALL-06: sign-up is one phone screen per step, so its band above and below the
            card is py-4 on phones (log in and the rest keep py-10). Desktop is unchanged. */}
        <main
          className={`flex flex-1 items-center justify-center px-5 group-data-[signup]:max-[24.375rem]:py-2 lg:py-16 ${isSignup ? "py-4" : "py-10"}`}
        >
          <div className="@container w-full max-w-[460px]">
            <div className="rounded-3xl bg-white p-7 [overflow-wrap:anywhere] @max-[16rem]:p-4 group-data-[signup]:@min-[16rem]:max-[24.375rem]:py-5 @max-[16rem]:hyphens-auto @max-[16rem]:[&_h1]:text-xl shadow-xl ring-1 ring-neutral-100 sm:p-10 lg:shadow-none lg:ring-0 lg:p-0 dark:bg-card dark:ring-border dark:lg:bg-transparent dark:lg:ring-0">
              <Outlet />
            </div>
            {!isSignup && (
              <p className="mt-6 text-center text-[11px] text-neutral-500 dark:text-muted-foreground">
                By continuing, you agree to our{" "}
                <Link
                  to="/terms"
                  className="font-medium text-neutral-700 hover:underline dark:text-foreground"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="font-medium text-neutral-700 hover:underline dark:text-foreground"
                >
                  Privacy Policy
                </Link>
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
